import { useState, useEffect, useCallback } from "react";
import { httpClient } from "../api/dataProvider";
import { baseiUrl } from "../utils/constants";
import { _log } from "../utils/logUtils";

// Stage 0: The "Good old Map" (Module-level, survives re-renders)
const allLogsMap = new Map();

export const useVehicleHistory = (selectedId, token) => {
    const [todayLogs, setTodayLogs] = useState([]);
    const [yesterdayLogs, setYesterdayLogs] = useState([]);
    const [oldLogs, setOldLogs] = useState([]);

    const hydrateLogs = useCallback(
        (vehicleId, incomingLogs = []) => {
            // 1. Get/Merge and Clean logs
            const existing = allLogsMap.get(vehicleId) || [];
            const combined = [...existing, ...incomingLogs];

            // Deduplicate by ID and remove logs older than 3 days
            // const limit = new Date(Date.now() - 3 * 86400000).getTime();
            const startOfToday = new Date().setHours(0, 0, 0, 0);
            const msPerDay = 86400000;
            const limit = startOfToday - 2 * msPerDay;
            const seen = new Set();
            const cleanLogs = combined
                .filter(l => {
                    const ts = new Date(l.logDate).getTime();
                    if (ts < limit || seen.has(l.id)) return false;
                    seen.add(l.id);
                    return true;
                })
                // .sort((a, b) => new Date(a.logDate) - new Date(b.logDate));
                .sort((a, b) => new Date(a.logDate).getTime() - new Date(b.logDate).getTime());

            // Calculate/Update Ages
            // const now = new Date();
            // const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).getTime();
            // const msPerDay = 86400000;

            const logsWithAge = cleanLogs.map(l => {
                const ts = new Date(l.logDate).getTime();
                let age = 0;
                if (ts < startOfToday - msPerDay) age = 2;
                else if (ts < startOfToday) age = 1;
                return { ...l, age };
            });

            allLogsMap.set(vehicleId, logsWithAge);

            // 2. If selected, split and update UI states
            if (vehicleId === selectedId) {
                setTodayLogs(logsWithAge.filter(l => l.age === 0));
                setYesterdayLogs(logsWithAge.filter(l => l.age === 1));
                setOldLogs(logsWithAge.filter(l => l.age === 2));
            }

            // 3. Save Master Map to LocalStorage (Trimmed to 10k logs total across all vehicles)
            const allData = Array.from(allLogsMap.entries());
            const storageObject = {};
            let totalCount = 0;

            for (const [id, logs] of allData) {
                if (totalCount > 10000) break;
                // storageObject[id] = logs.slice(-2000).map(({ id, logDate, coordinate }) => ({ id, logDate, coordinate }));
                storageObject[id] = logs.slice(-2000);
                totalCount += storageObject[id].length;
            }
            localStorage.setItem("vlogs_master", JSON.stringify(storageObject));
        },
        [selectedId],
    );

    const fetchLogs = useCallback(
        async vehicleId => {
            if (!token) return;
            const vehicleLogs = allLogsMap.get(vehicleId) || [];
            const day0 = vehicleLogs.filter(l => l.age === 0);
            const day1 = vehicleLogs.filter(l => l.age === 1);
            const day2 = vehicleLogs.filter(l => l.age === 2);

            // API 1: Today (Delta or Full)
            const lastDate = day0.length > 0 ? day0[day0.length - 1].logDate : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
            const todayRes = await httpClient(`${baseiUrl}/vehicles/log/${vehicleId}&${lastDate}&${new Date().toISOString()}`, localStorage.getItem("tetherauth"));
            // if (todayRes.json?.length) hydrateLogs(vehicleId, todayRes.json);
            const todayClean = (todayRes.json || []).map(l => ({ id: l._id, logDate: l.logDate, coordinate: l.coordinate }));
            if (todayClean.length > 0) hydrateLogs(vehicleId, todayClean);

            // API 2: Yesterday (If empty)
            if (day1.length === 0) {
                const start = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
                const end = new Date().toISOString().slice(0, 10);
                const res = await httpClient(`${baseiUrl}/vehicles/log/${vehicleId}&${start}&${end}`, localStorage.getItem("tetherauth"));
                // if (res.json?.length) hydrateLogs(vehicleId, res.json);
                const histClean = (res.json || []).map(l => ({ id: l._id, logDate: l.logDate, coordinate: l.coordinate }));
                if (histClean.length > 0) hydrateLogs(vehicleId, histClean);
            }

            // API 3: Old (If empty)
            if (day2.length === 0) {
                const start = new Date(Date.now() - 172800000).toISOString().slice(0, 10);
                const end = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
                const res = await httpClient(`${baseiUrl}/vehicles/log/${vehicleId}&${start}&${end}`, localStorage.getItem("tetherauth"));
                // if (res.json?.length) hydrateLogs(vehicleId, res.json);
                const histClean = (res.json || []).map(l => ({ id: l._id, logDate: l.logDate, coordinate: l.coordinate }));
                if (histClean.length > 0) hydrateLogs(vehicleId, histClean);
            }
        },
        [token, hydrateLogs],
    );

    // Initial Startup: getCache()
    useEffect(() => {
        try {
            const cached = localStorage.getItem("vlogs_master");
            if (cached) {
                const data = JSON.parse(cached);
                // Object.keys(data).forEach(vid => hydrateLogs(vid, data[vid]));
                Object.keys(data).forEach(vid =>
                    hydrateLogs(
                        vid,
                        data[vid].map(l => ({ id: l.id, logDate: l.logDate, coordinate: l.coordinate })),
                    ),
                );
            }
        } catch (e) {
            localStorage.removeItem("vlogs_master");
        }
    }, []); // Run once on module load

    // Trigger on selection change
    useEffect(() => {
        if (!selectedId) {
            setTodayLogs([]);
            setYesterdayLogs([]);
            setOldLogs([]);
            return;
        }
        // Immediate UI refresh from existing map data
        hydrateLogs(selectedId, []);
        // Background network sync
        fetchLogs(selectedId);
    }, [selectedId, fetchLogs, hydrateLogs]);

    return { todayLogs, yesterdayLogs, oldLogs, fetchLogs, hydrateLogs };
};
